import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { apiService } from '../../services/apiService';
import type { CurationLimits, Keyword, KeywordStats } from '../../types/keywords';
import { ConfirmDialog } from './ConfirmDialog';
import { CurationGuidelines } from './CurationGuidelines';
import { KeywordDialog } from './KeywordDialog';
import type { KeywordVocabulary } from './useKeywordVocabulary';
import { formatShare } from './utils';

type Editing = { keyword: Keyword | null } | null;

export function KeywordsTab({ vocabulary }: { vocabulary: KeywordVocabulary }) {
  const { t } = useTranslation('admin');
  const { draft, active, save, reload } = vocabulary;
  const [stats, setStats] = useState<Map<string, KeywordStats>>(new Map());
  const [limits, setLimits] = useState<CurationLimits | null>(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Editing>(null);
  const [mergeSource, setMergeSource] = useState<Keyword | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    apiService.getKeywordStats().then(
      (response) => {
        setStats(new Map(response.keywords.map((s) => [s.keyword_id, s])));
        setLimits(response.limits);
      },
      () => setStats(new Map()), // stats and guidelines are optional decoration
    );
  }, []);

  const activeIds = useMemo(() => new Set(active?.keywords.map((k) => k.id)), [active]);
  const keywords = useMemo(() => draft?.keywords ?? [], [draft]);
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return keywords;
    return keywords.filter((k) =>
      [k.id, ...Object.values(k.labels), ...k.synonyms].some((text) => text.toLowerCase().includes(needle))
    );
  }, [keywords, filter]);
  // Shows whether manually created keywords end up outside the limits more often (§11.3).
  const originSummary = useMemo(
    () =>
      (['proposal', 'manual'] as const).map((origin) => {
        const ofOrigin = keywords.filter((k) => k.origin === origin);
        const flagged = ofOrigin.filter((k) => stats.get(k.id)?.too_rare || stats.get(k.id)?.too_broad);
        return { origin, count: ofOrigin.length, flagged: flagged.length };
      }),
    [keywords, stats]
  );

  if (!draft) return null;

  const update = (next: Keyword[], merges = draft.merges) => save({ ...draft, keywords: next, merges });
  // Merge sources are gone from the draft, so fall back to the active list.
  const labelOf = (id: string) =>
    [...keywords, ...(active?.keywords ?? [])].find((k) => k.id === id)?.labels.en ?? id;

  const handleSave = async (keyword: Keyword) => {
    const exists = keywords.some((k) => k.id === keyword.id);
    const next = exists ? keywords.map((k) => (k.id === keyword.id ? keyword : k)) : [...keywords, keyword];
    if (await update(next)) setEditing(null);
  };

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    const next = keywords.filter((k) => k.id !== mergeSource.id);
    if (await update(next, [...draft.merges, { from_id: mergeSource.id, to_id: mergeTarget }])) {
      setMergeSource(null);
      setMergeTarget('');
    }
  };

  const handleDiscard = async () => {
    try {
      await apiService.resetKeywordDraft();
      await reload();
    } finally {
      setConfirmDiscard(false);
    }
  };

  return (
    <Box>
      {limits && <CurationGuidelines limits={limits} defaultExpanded={keywords.length === 0} />}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label={t('keywords.filter')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('keywords.count', { count: keywords.length })}
          </Typography>
          {keywords.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {originSummary
                .map(({ origin, count, flagged }) => t('keywords.originSummary', {
                  origin: t(`origins.${origin}`), count, flagged,
                }))
                .join(' · ')}
            </Typography>
          )}
        </Box>
        <Button onClick={() => setConfirmDiscard(true)} disabled={!vocabulary.etag}>
          {t('keywords.discard')}
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ keyword: null })}>
          {t('keywords.add')}
        </Button>
      </Stack>

      {draft.merges.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('keywords.pendingMerges')}{' '}
          {draft.merges.map((m) => `${labelOf(m.from_id)} → ${labelOf(m.to_id)}`).join(', ')}
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('keywords.labelEn')}</TableCell>
            <TableCell>{t('keywords.labelDe')}</TableCell>
            <TableCell>{t('keywords.definition')}</TableCell>
            <TableCell align="right">{t('keywords.documents')}</TableCell>
            <TableCell />
            <TableCell align="right">{t('keywords.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((keyword) => {
            const keywordStats = stats.get(keyword.id);
            const isNew = !activeIds.has(keyword.id);
            return (
              <TableRow key={keyword.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{keyword.labels.en}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {keyword.id} · {t(`origins.${keyword.origin}`)}
                  </Typography>
                </TableCell>
                <TableCell>{keyword.labels.de}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2">{keyword.definition}</Typography>
                  {keyword.synonyms.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('keywords.synonyms')}: {keyword.synonyms.join(', ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {keywordStats && `${keywordStats.doc_count} (${formatShare(keywordStats.share)})`}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {isNew && <Chip size="small" color="primary" label={t('badges.new')} />}
                    {keyword.reevaluate && <Chip size="small" color="info" label={t('badges.reevaluate')} />}
                    {keywordStats?.too_rare && <Chip size="small" color="warning" label={t('badges.tooRare')} />}
                    {keywordStats?.too_broad && <Chip size="small" color="warning" label={t('badges.tooBroad')} />}
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={t('keywords.edit')}>
                    <IconButton size="small" onClick={() => setEditing({ keyword })}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('keywords.mergeInto')}>
                    <span>
                      <IconButton size="small" onClick={() => setMergeSource(keyword)} disabled={isNew}>
                        <CallMergeIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('keywords.reevaluateHint')}>
                    <span>
                      <IconButton
                        size="small"
                        color={keyword.reevaluate ? 'primary' : 'default'}
                        disabled={isNew}
                        onClick={() =>
                          update(keywords.map((k) => (k.id === keyword.id ? { ...k, reevaluate: !k.reevaluate } : k)))
                        }
                      >
                        <AutorenewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('keywords.delete')}>
                    <IconButton size="small" onClick={() => update(keywords.filter((k) => k.id !== keyword.id))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {keywords.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {t('keywords.empty')}
        </Typography>
      )}

      {editing && (
        <KeywordDialog
          key={editing.keyword?.id ?? 'new'}
          keyword={editing.keyword}
          existingIds={new Set(keywords.map((k) => k.id))}
          labelOf={labelOf}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={mergeSource !== null}
        title={t('keywords.mergeTitle', { label: mergeSource?.labels.en })}
        confirmLabel={t('keywords.mergeInto')}
        onConfirm={handleMerge}
        onClose={() => setMergeSource(null)}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>{t('keywords.mergeHint')}</Typography>
        <TextField
          select
          fullWidth
          label={t('keywords.mergeTarget')}
          value={mergeTarget}
          onChange={(e) => setMergeTarget(e.target.value)}
        >
          {keywords
            .filter((k) => k.id !== mergeSource?.id)
            .map((k) => (
              <MenuItem key={k.id} value={k.id}>{k.labels.en}</MenuItem>
            ))}
        </TextField>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmDiscard}
        title={t('keywords.discardTitle')}
        confirmLabel={t('keywords.discard')}
        onConfirm={handleDiscard}
        onClose={() => setConfirmDiscard(false)}
      >
        {t('keywords.discardHint')}
      </ConfirmDialog>
    </Box>
  );
}
